import produce from 'immer'
import { CaseworkerOpenReportAction } from '../actions/'
import { CaseworkerOpenReportActionType } from '../action-types'
import { ApiIncident, ChatMessage } from '../../ts/api-types'

interface IdleState {
    type: 'idle'
}

interface LoadingState {
    type: 'loading'
}

interface ErrorState {
    type: 'error'
    error: string
}

export interface LoadedState {
    type: 'loaded'
    openReport: ApiIncident
    openReportMessagesLoading: boolean
    openReportMessagesError: string | null
    openReportMessages: ChatMessage[]
    addMessageLoading: boolean
    changeResponsibleLoading: boolean
    updateCategoryLoading: boolean
    forwardReportLoading: boolean
    forwardReportError: string | null
    updateStatusLoading: boolean
    closeReportLoading: boolean
    reopenReportLoading: boolean
    reopenReportError: string | null
}

export type CaseworkerOpenReportState =
    | IdleState
    | LoadingState
    | ErrorState
    | LoadedState

const initialState: CaseworkerOpenReportState = {
    type: 'idle',
}

const makeLoadedState = (openReport: ApiIncident): LoadedState => ({
    type: 'loaded',
    openReport,
    openReportMessagesLoading: false,
    openReportMessagesError: null,
    openReportMessages: [],
    addMessageLoading: false,
    changeResponsibleLoading: false,
    updateCategoryLoading: false,
    forwardReportLoading: false,
    forwardReportError: null,
    updateStatusLoading: false,
    closeReportLoading: false,
    reopenReportLoading: false,
    reopenReportError: null,
})

const reducer = produce(
    (
        state: CaseworkerOpenReportState = initialState,
        action: CaseworkerOpenReportAction
    ): CaseworkerOpenReportState => {
        switch (action.type) {
            // --- Initial fetch lifecycle: return new state objects ---

            case CaseworkerOpenReportActionType.SET_GET_SINGLE_REPORT_LOADING:
                return { type: 'loading' }

            case CaseworkerOpenReportActionType.SET_GET_SINGLE_REPORT_ERROR:
                return { type: 'error', error: action.payload }

            case CaseworkerOpenReportActionType.GET_SINGLE_REPORT:
                return makeLoadedState(action.payload)

            case CaseworkerOpenReportActionType.LEAVE_REPORT:
                return { type: 'idle' }

            // --- All actions below only apply when report is loaded ---

            case CaseworkerOpenReportActionType.SET_GET_REPORT_MESSAGES_LOADING:
                if (state.type !== 'loaded') return state
                state.openReportMessagesLoading = true
                return state

            case CaseworkerOpenReportActionType.SET_GET_REPORT_MESSAGES_ERROR:
                if (state.type !== 'loaded') return state
                state.openReportMessagesError = action.payload
                return state

            case CaseworkerOpenReportActionType.GET_REPORT_MESSAGES:
                if (state.type !== 'loaded') return state
                state.openReportMessagesLoading = false
                state.openReportMessagesError = null
                state.openReportMessages = [
                    ...state.openReportMessages,
                    ...action.payload,
                ].sort(function (
                    a: { created_at: string },
                    b: { created_at: string }
                ) {
                    return (
                        new Date(a.created_at).getTime() -
                        new Date(b.created_at).getTime()
                    )
                })
                return state

            case CaseworkerOpenReportActionType.SET_ADD_MESSAGE_LOADING:
                if (state.type !== 'loaded') return state
                state.addMessageLoading = true
                return state

            case CaseworkerOpenReportActionType.ADD_MESSAGE:
                if (state.type !== 'loaded') return state
                state.addMessageLoading = false
                return state

            case CaseworkerOpenReportActionType.SHOW_LATEST_MESSAGE:
                if (state.type !== 'loaded') return state
                // TODO handle loading
                state.openReportMessages.push(action.payload)
                return state

            case CaseworkerOpenReportActionType.SHOW_LATEST_FILES:
                if (state.type !== 'loaded') return state
                if (state.openReport.files) {
                    state.openReport.files.data.push(...action.payload)
                }
                return state

            case CaseworkerOpenReportActionType.SET_CHANGE_RESPONSIBLE_LOADING:
                if (state.type !== 'loaded') return state
                state.changeResponsibleLoading = true
                return state

            case CaseworkerOpenReportActionType.CHANGE_RESPONSIBLE:
                if (state.type !== 'loaded') return state
                state.changeResponsibleLoading = false
                return state

            case CaseworkerOpenReportActionType.SET_UPDATE_CATEGORY_LOADING:
                if (state.type !== 'loaded') return state
                state.updateCategoryLoading = true
                return state

            case CaseworkerOpenReportActionType.UPDATE_CATEGORY:
                if (state.type !== 'loaded') return state
                state.updateCategoryLoading = false
                state.openReport.category = action.payload.name
                state.openReport.category_id = action.payload.id
                return state

            case CaseworkerOpenReportActionType.UPDATE_SELECTED_CASEWORKER:
                if (state.type !== 'loaded') return state
                state.openReport.caseworkers.data.push(action.payload)
                return state

            case CaseworkerOpenReportActionType.SET_UPDATE_STATUS_LOADING:
                if (state.type !== 'loaded') return state
                state.updateStatusLoading = true
                return state

            case CaseworkerOpenReportActionType.UPDATE_STATUS:
                if (state.type !== 'loaded') return state
                state.updateStatusLoading = false
                state.openReport.status = action.payload
                return state

            case CaseworkerOpenReportActionType.SET_CLOSE_REPORT_LOADING:
                if (state.type !== 'loaded') return state
                state.closeReportLoading = true
                return state

            case CaseworkerOpenReportActionType.SET_FORWARD_REPORT_LOADING:
                if (state.type !== 'loaded') return state
                state.forwardReportLoading = true
                return state

            case CaseworkerOpenReportActionType.SET_FORWARD_REPORT_ERROR:
                if (state.type !== 'loaded') return state
                state.forwardReportError = action.payload
                return state

            case CaseworkerOpenReportActionType.FORWARD_REPORT:
                if (state.type !== 'loaded') return state
                state.forwardReportLoading = false
                state.forwardReportError = null
                return state

            case CaseworkerOpenReportActionType.CLOSE_REPORT:
                if (state.type !== 'loaded') return state
                state.openReport.closed_at = action.payload.closed_at
                state.openReport.closed_comment = action.payload.closed_comment
                state.openReport.deletion_date = action.payload.deletion_date
                return state

            case CaseworkerOpenReportActionType.SHOW_SPECIAL_FORWARDED_MESSAGE:
                if (state.type !== 'loaded') return state
                state.openReportMessages.push(action.payload)
                return state

            case CaseworkerOpenReportActionType.SET_REOPEN_REPORT_LOADING:
                if (state.type !== 'loaded') return state
                state.reopenReportLoading = true
                return state

            case CaseworkerOpenReportActionType.SET_REOPEN_REPORT_ERROR:
                if (state.type !== 'loaded') return state
                state.reopenReportError = action.payload
                return state

            case CaseworkerOpenReportActionType.REOPEN_REPORT:
                if (state.type !== 'loaded') return state
                state.openReport.closed_at = null
                state.openReport.closed_comment = null
                state.reopenReportLoading = false
                state.reopenReportError = null
                return state

            default:
                return state
        }
    }
)

export default reducer
